import { dexieDB, IDexieFile } from "@/services/dexie-database";
import { postBookingFile, postTicketFile } from "@/api/pos-files/pos-files";
import { getBookingWithUUID } from "@/api/gql/product-slot-boooking";
import { getPSSByUUID } from "@/api/gql/product-slot-state";
import { LogLevel, LogService } from "@/services/logging";
import { SyncStatus } from "@/types/db/product-slot-booking";

/**
 * Sync all pending/failed files from Dexie to backend.
 * Processes one file at a time. Deletes the file blob from Dexie on success.
 */
export async function syncDexiePosFiles(): Promise<void> {
  if (!dexieDB.isOpen()) {
    LogService.info("PosFiles Sync: Database not open, skipping");
    return;
  }

  // Get files that are ready to sync (have syncStatus=Pending and bookingUuid or pssUuid set)
  const filesToSync = await dexieDB.files
    .where("syncStatus")
    .equals(SyncStatus.Pending)
    .toArray();

  if (!filesToSync.length) {
    return;
  }

  LogService.info(`PosFiles Sync: Found ${filesToSync.length} files to sync`);

  for (const file of filesToSync) {
    try {
      await syncSingleFile(file);
    } catch (error) {
      LogService.info(
        `PosFiles Sync: Error syncing file ${file._id}`,
        { error },
        LogLevel.Error
      );
      // Continue with next file — don't let one failure block others
    }
  }
}

/**
 * Sync a single file to the backend, then delete from Dexie on success.
 */
async function syncSingleFile(file: IDexieFile): Promise<void> {
  if (!file.file) {
    // No blob data — nothing to upload, clean up the record
    LogService.info(
      `PosFiles Sync: File ${file._id} has no blob data, deleting record`
    );
    await dexieDB.files.where({ _id: file._id }).delete();
    return;
  }

  if (!file.bookingUuid && !file.pssUuid) {
    // No booking/pss UUID set yet — insertFilesIDs hasn't been called yet, skip
    return;
  }

  // 1. Resolve booking/pss UUID to bookingId/pssId via GraphQL
  const bookingInfo: {
    bookingToken: string;
    bookingId?: number;
    pssId?: number;
  } = { bookingToken: "" };

  if (file.pssUuid) {
    LogService.info(
      `PosFiles Sync: Resolving pssId for file ${file._id}, pssUuid: ${file.pssUuid}`
    );
    const resp = await getPSSByUUID(file.pssUuid);
    if (resp) {
      if (resp.booking) {
        if (resp.booking.token) {
          bookingInfo.bookingToken = resp.booking.token;
        }
        if (resp.booking.id) {
          bookingInfo.bookingId = resp.booking.id;
        }
      }
      if (resp.id) {
        bookingInfo.pssId = resp.id;
      }
    }
  } else if (file.bookingUuid) {
    LogService.info(
      `PosFiles Sync: Resolving bookingId for file ${file._id}, bookingUuid: ${file.bookingUuid}`
    );
    const resp = await getBookingWithUUID(file.bookingUuid);
    if (resp) {
      if (resp.token) {
        bookingInfo.bookingToken = resp.token;
      }
      if (resp.id) {
        bookingInfo.bookingId = resp.id;
      }
    }
  }

  // 2. POST the file to the appropriate endpoint
  if (bookingInfo.pssId) {
    // Ticket-level file
    LogService.info(
      `PosFiles Sync: Posting ticket file ${file._id} for pssId: ${bookingInfo.pssId}`
    );
    await postTicketFile(
      Number(file.customFieldId),
      file.file,
      bookingInfo.bookingToken,
      Number(bookingInfo.bookingId),
      Number(bookingInfo.pssId)
    );
  } else {
    // Booking-level file
    LogService.info(
      `PosFiles Sync: Posting booking file ${file._id} for bookingId: ${bookingInfo.bookingId}`
    );
    await postBookingFile(
      Number(file.customFieldId),
      file.file,
      bookingInfo.bookingToken,
      Number(bookingInfo.bookingId)
    );
  }

  // 3. Delete the file blob from Dexie on success
  await dexieDB.files.where({ _id: file._id }).delete();
  LogService.info(
    `PosFiles Sync: File ${file._id} synced and deleted successfully`
  );
}
